import * as React from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RefreshCw, Truck, PackageCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/data/EmptyState';
import { DateTime } from '@/components/data/DateTime';
import { ShortCode } from '@/components/data/ShortCode';
import { StateMachineButtonGroup, type StateMachineTransition } from '@/components/data/StateMachineButtonGroup';
import { AuditChip } from '@/components/audit/AuditChip';
import { Alert } from '@/components/ui/Alert';
import { ShipDialog } from '@/features/commerce-ops/ShipDialog';
import { RefundDialog } from '@/features/commerce-ops/RefundDialog';
import { CustomerRevealPanel } from '@/features/commerce-ops/CustomerRevealPanel';
import { orderStateTone } from '@/features/commerce-ops/orderState';
import { useFulfillOrder, useOrderDetail } from '@/queries/commerceOps';
import { useApiError } from '@/lib/useApiError';
import { ApiError } from '@/api/errors';
import { formatCents } from '@/lib/money';
import { useUiStore } from '@/stores/ui';
import type { OrderState } from '@/schemas/admin/commerce/orders';

export const Route = createFileRoute('/_authed/commerce/orders/$id')({
  component: OrderDetailScreen,
});

function OrderDetailScreen() {
  const { id } = Route.useParams();
  const { t } = useTranslation('commerce');
  const { t: tc } = useTranslation('common');
  const order = useOrderDetail(id);
  const fulfill = useFulfillOrder();
  const resolveError = useApiError();
  const locale = useUiStore((s) => s.locale);

  const [shipOpen, setShipOpen] = React.useState(false);
  const [refundOpen, setRefundOpen] = React.useState(false);
  const [transitionedBanner, setTransitionedBanner] = React.useState(false);

  if (order.isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-6 w-1/3 animate-pulse rounded bg-[color:var(--color-surface-2)]" />
        <div className="h-32 animate-pulse rounded bg-[color:var(--color-surface-2)]" />
      </div>
    );
  }

  if (order.error || !order.data) {
    const resolved = order.error ? resolveError(order.error) : null;
    return (
      <EmptyState
        variant="error"
        title={resolved?.message ?? tc('errors.notFoundTitle')}
        body={resolved?.recovery ?? tc('errors.notFoundBody')}
        action={
          <Button asChild variant="ghost">
            <Link to="/commerce/orders">{t('orders.detail.back')}</Link>
          </Button>
        }
      />
    );
  }

  const o = order.data;
  const refundable: OrderState[] = ['paid', 'fulfilling', 'shipped', 'delivered'];

  async function handleFulfill() {
    if (!o) return;
    try {
      await fulfill.mutateAsync(o.id);
      toast.success(t('orders.detail.fulfillSuccess'));
    } catch (err) {
      // 409 invalid_transition → refetch + banner.
      if (err instanceof ApiError && (err.code === 'invalid_transition' || err.status === 409)) {
        setTransitionedBanner(true);
        void order.refetch();
        return;
      }
      const resolved = resolveError(err);
      toast.error(
        resolved.message,
        resolved.recovery ? { description: resolved.recovery } : undefined
      );
    }
  }

  const transitions: StateMachineTransition<OrderState>[] = [
    {
      to: 'fulfilling',
      label: t('orders.actions.fulfill'),
      from: ['paid'],
      icon: PackageCheck,
      action: handleFulfill,
    },
    {
      to: 'shipped',
      label: t('orders.actions.ship'),
      from: ['paid', 'fulfilling'],
      icon: Truck,
      action: () => setShipOpen(true),
    },
    {
      to: 'refunded',
      label: t('orders.actions.refund'),
      from: refundable,
      icon: Undo2,
      variant: 'danger',
      action: () => setRefundOpen(true),
    },
  ];

  const subtotal =
    typeof o.subtotal_cents === 'number'
      ? o.subtotal_cents
      : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/commerce/orders">
            <ArrowLeft aria-hidden className="size-4 rtl:rotate-180" />
            {t('orders.detail.back')}
          </Link>
        </Button>
      </div>

      {transitionedBanner ? (
        <Alert variant="warn" className="flex items-center justify-between gap-3">
          <span>{t('orders.detail.transitionedBanner')}</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setTransitionedBanner(false);
                void order.refetch();
              }}
            >
              <RefreshCw aria-hidden className="size-4" />
              {tc('actions.retry')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setTransitionedBanner(false)}>
              {t('orders.detail.transitionedBannerDismiss')}
            </Button>
          </div>
        </Alert>
      ) : null}

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              <ShortCode code={o.short_code} copyable />
            </h1>
            <Badge tone={orderStateTone(o.state)}>{t(`orders.states.${o.state}`)}</Badge>
          </div>
          <p className="text-sm text-[color:var(--color-fg-muted)]">
            <DateTime value={o.placed_at ?? o.created_at ?? null} />
            <span className="ms-2 font-mono">
              {formatCents(o.total_cents, locale, o.currency)}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AuditChip />
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <h2 className="text-sm font-semibold">{tc('table.actions')}</h2>
          <StateMachineButtonGroup<OrderState>
            currentState={o.state}
            transitions={transitions}
          />
        </CardContent>
      </Card>

      <CustomerRevealPanel orderId={o.id} />

      <Card>
        <CardContent className="space-y-3 pt-6">
          <h2 className="text-sm font-semibold">{t('orders.detail.lineItems')}</h2>
          {o.items && o.items.length > 0 ? (
            <ul className="divide-y divide-[color:var(--color-border)]">
              {o.items.map((item) => (
                <li key={item.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{item.product_name ?? '—'}</div>
                    <div className="text-xs text-[color:var(--color-fg-muted)]">
                      {[item.variant_label, item.designer_name].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-3 text-sm">
                    <span className="text-[color:var(--color-fg-muted)]">×{item.quantity}</span>
                    <span dir="ltr" className="font-mono">
                      {formatCents(item.line_total_cents, locale, o.currency)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[color:var(--color-fg-muted)]">
              {t('orders.detail.lineItemsEmpty')}
            </p>
          )}

          {subtotal !== null ||
          typeof o.shipping_cents === 'number' ||
          typeof o.tax_cents === 'number' ? (
            <div className="mt-4 grid grid-cols-2 gap-y-1 border-t border-[color:var(--color-border)] pt-3 text-sm sm:max-w-sm sm:ms-auto">
              {subtotal !== null ? (
                <>
                  <span className="text-[color:var(--color-fg-muted)]">
                    {t('orders.detail.totals.subtotal')}
                  </span>
                  <span dir="ltr" className="text-end font-mono">
                    {formatCents(subtotal, locale, o.currency)}
                  </span>
                </>
              ) : null}
              {typeof o.shipping_cents === 'number' ? (
                <>
                  <span className="text-[color:var(--color-fg-muted)]">
                    {t('orders.detail.totals.shipping')}
                  </span>
                  <span dir="ltr" className="text-end font-mono">
                    {formatCents(o.shipping_cents, locale, o.currency)}
                  </span>
                </>
              ) : null}
              {typeof o.tax_cents === 'number' ? (
                <>
                  <span className="text-[color:var(--color-fg-muted)]">
                    {t('orders.detail.totals.tax')}
                  </span>
                  <span dir="ltr" className="text-end font-mono">
                    {formatCents(o.tax_cents, locale, o.currency)}
                  </span>
                </>
              ) : null}
              <span className="font-semibold">{t('orders.detail.totals.total')}</span>
              <span dir="ltr" className="text-end font-mono font-semibold">
                {formatCents(o.total_cents, locale, o.currency)}
              </span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <h2 className="text-sm font-semibold">{t('orders.detail.shipments')}</h2>
          {o.shipments && o.shipments.length > 0 ? (
            <ul className="space-y-2">
              {o.shipments.map((s) => (
                <li
                  key={s.id}
                  className="flex flex-wrap items-baseline justify-between gap-2 rounded-[var(--radius-md)] border border-[color:var(--color-border)] p-3"
                >
                  <div>
                    <div className="text-sm font-medium">{s.carrier ?? '—'}</div>
                    <div dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-muted)]">
                      {s.tracking_number ?? '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone="info">{s.state}</Badge>
                    <DateTime value={s.shipped_at ?? null} />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[color:var(--color-fg-muted)]">
              {t('orders.detail.shipmentsEmpty')}
            </p>
          )}
        </CardContent>
      </Card>

      <ShipDialog
        orderId={o.id}
        open={shipOpen}
        onOpenChange={setShipOpen}
        onShipped={() => void order.refetch()}
      />
      <RefundDialog
        orderId={o.id}
        totalCents={o.total_cents}
        currency={o.currency}
        open={refundOpen}
        onOpenChange={setRefundOpen}
        onRefunded={() => void order.refetch()}
      />
    </div>
  );
}
